import {
  AnalyticsPageName,
  BacktestingRunType,
  BotGraphType,
} from "@shared/types";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import React from "react";
import styled, { useTheme } from "styled-components";
import { Calendar } from "lucide-react";
import Flex from "common/Boxes/Flex";
import CalendarOutlined from "@ant-design/icons/CalendarOutlined";
import { getDisplayedTimeframe } from "@shared/features/bots/utils";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import BacktestingChart from "features/bots/BotForm/Backtesting/common/BacktestingChart";

interface BacktestingGraphProps {
  backtestingRun?: BacktestingRunType;
}

const BacktestingGraph = ({ backtestingRun }: BacktestingGraphProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <>
        <NewLookHeader>
          <NewLookTitle>
            {t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT])}
            <HelperTooltip
              description={t("backtesting.helper_tooltips.accumulated_profit")}
              analyticsPageName={AnalyticsPageName.BACKTESTING}
              analyticsFieldName="Accumulated Profit"
            />
          </NewLookTitle>
          {backtestingRun?.backtesting_time_range && (
            <NewLookTimeframe>
              <Calendar size={14} strokeWidth={1.75} />
              {getDisplayedTimeframe(backtestingRun?.backtesting_time_range)}
            </NewLookTimeframe>
          )}
        </NewLookHeader>
        <ChartWrapper>
          <BacktestingChart backtestingRun={backtestingRun} />
        </ChartWrapper>
      </>
    );
  }

  return (
    <>
      <Flex
        $flexDirection={isSmallScreen ? "column" : "row"}
        $alignItems={isSmallScreen ? "flex-start" : "center"}
      >
        <Title level={isMobile ? 4 : 3}>
          {t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT])}
          <HelperTooltip
            description={t("backtesting.helper_tooltips.accumulated_profit")}
            analyticsPageName={AnalyticsPageName.BACKTESTING}
            analyticsFieldName="Accumulated Profit"
          />
        </Title>
        {backtestingRun?.backtesting_time_range && (
          <Text
            type="secondary"
            style={{ alignSelf: "flex-end", marginTop: isSmallScreen ? 8 : 0 }}
          >
            <CalendarOutlined
              style={{
                fontSize: isMobile ? 14 : 18,
                marginRight: isMobile ? 4 : 8,
              }}
            />
            {getDisplayedTimeframe(backtestingRun?.backtesting_time_range)}
          </Text>
        )}
      </Flex>
      <ChartWrapper>
        <BacktestingChart backtestingRun={backtestingRun} />
      </ChartWrapper>
    </>
  );
};

export default BacktestingGraph;

const ChartWrapper = styled.div`
  height: 318px;
  margin-top: 16px;
`;
const NewLookHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
`;
const NewLookTitle = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 15px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookTimeframe = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;
